/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from "react";
import { Language, UITranslations, SkillCategory } from "../Data/types.ts";
import { translations } from "../Data/sectionTranslations.ts";

interface LanguageContextType {
  currentLang: Language;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  t: (key: keyof UITranslations | SkillCategory) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

const getInitialLanguage = (): Language => {
  const path = window.location.pathname;
  if (path.startsWith("/zh")) return "zh";

  const savedLang = localStorage.getItem("language");
  if (savedLang === "en" || savedLang === "zh") return savedLang;

  return navigator.language.toLowerCase().startsWith("zh") ? "zh" : "en";
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentLang, setCurrentLang] = useState<Language>(getInitialLanguage);

  const setLanguage = (lang: Language) => setCurrentLang(lang);

  const toggleLanguage = () =>
    setCurrentLang((prev) => (prev === "en" ? "zh" : "en"));

  const t = (key: keyof UITranslations | SkillCategory): string =>
    translations[currentLang][key];

  return (
    <LanguageContext.Provider
      value={{ currentLang, toggleLanguage, setLanguage, t }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
